import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import {
  TopHeader,
  Button,
  Alert,
  Select,
  ConfirmModal,
} from '../../components';
import { useInvites, useAllGuests, useAllergySummary, usePurgeAllergyData, useKeepAllergyData } from '../../api/hooks/useInvites';
import { useToast } from '../../contexts/ToastContext';

const KEEP_REASON_OPTIONS = [
  { value: 'another_event', label: 'I plan to have another event soon' },
  { value: 'venue_caterer', label: 'My venue or caterer still needs it' },
  { value: 'postponed', label: 'My event has been postponed' },
  { value: 'other', label: 'Other reason' },
];

function formatEventDate(dateStr: string | null): string {
  if (!dateStr) return 'No date set';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function AllergyDataPage() {
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const { data: invites } = useInvites();
  const { data: allGuests } = useAllGuests();

  const invitesWithAllergy = useMemo(() => {
    if (!invites || !allGuests) return [];
    const inviteIdsWithAllergy = new Set(
      allGuests
        .filter(g => g.allergies && g.allergies.length > 0)
        .map(g => g.invite.id)
    );
    return invites.filter(inv => inviteIdsWithAllergy.has(inv.id));
  }, [invites, allGuests]);

  const urlInviteId = searchParams.get('invite') ? Number(searchParams.get('invite')) : undefined;

  const defaultInviteId = useMemo(() => {
    if (urlInviteId) return urlInviteId;
    if (!invites) return undefined;
    const now = new Date();
    const pastWithAllergy = invitesWithAllergy.find(inv => inv.event_date && new Date(inv.event_date) < now);
    if (pastWithAllergy) return pastWithAllergy.id;
    if (invitesWithAllergy.length > 0) return invitesWithAllergy[0].id;
    return invites[0]?.id;
  }, [urlInviteId, invites, invitesWithAllergy]);

  const [selectedInviteId, setSelectedInviteId] = useState<number | undefined>(defaultInviteId);

  useEffect(() => {
    if (defaultInviteId && !selectedInviteId) {
      setSelectedInviteId(defaultInviteId);
    }
  }, [defaultInviteId, selectedInviteId]);

  const { data: summary, isLoading: summaryLoading } = useAllergySummary(selectedInviteId);
  const purge = usePurgeAllergyData();
  const keep = useKeepAllergyData();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showKeepForm, setShowKeepForm] = useState(false);
  const [keepReason, setKeepReason] = useState('');
  const [keepNotes, setKeepNotes] = useState('');
  const [deletedTitle, setDeletedTitle] = useState<string | null>(null);

  const inviteSelectOptions = useMemo(() => {
    return invitesWithAllergy.map(inv => ({ value: String(inv.id), label: inv.title }));
  }, [invitesWithAllergy]);

  const handleDelete = async () => {
    if (!selectedInviteId || !summary) return;
    const title = summary.title;
    await purge.mutateAsync(selectedInviteId);
    setShowDeleteModal(false);
    setDeletedTitle(title);
  };

  const handleKeepSubmit = async () => {
    if (!selectedInviteId || !keepReason) return;
    await keep.mutateAsync({ inviteId: selectedInviteId, reason: keepReason, notes: keepNotes || undefined });
    setShowKeepForm(false);
    setKeepReason('');
    setKeepNotes('');
    showToast('Retention request submitted', 'success');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopHeader title="Allergy Data" />

      <div className="flex-1 px-4 sm:px-8 py-8 max-w-2xl w-full mx-auto">
        {deletedTitle ? (
          <div className="bg-background-card border border-border rounded-2xl p-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mx-auto">
              <Check size={24} className="text-success" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary">Allergy data deleted</h2>
            <p className="text-sm text-text-secondary">All allergy information for <strong>{deletedTitle}</strong> has been removed. Names, RSVP status and contact details are untouched.</p>
          </div>
        ) : invitesWithAllergy.length === 0 ? (
          <Alert variant="info">No allergy data to manage — your guests haven't reported any allergies, or the data has already been deleted.</Alert>
        ) : (
          <>
            {invitesWithAllergy.length > 1 && (
              <div className="mb-6">
                <Select
                  label="Invite"
                  options={inviteSelectOptions}
                  value={selectedInviteId ? String(selectedInviteId) : ''}
                  onChange={(val) => {
                    setSelectedInviteId(Number(val));
                    setShowKeepForm(false);
                    setKeepReason('');
                    setKeepNotes('');
                  }}
                />
              </div>
            )}

            {summaryLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-text-secondary" />
              </div>
            ) : summary ? (
              <div className="bg-background-card border border-border rounded-2xl p-6 space-y-4">
                <div>
                  <h2 className="text-xl font-bold text-text-primary">{summary.title}</h2>
                  <p className="text-sm text-text-secondary mt-1">{formatEventDate(summary.event_date)}</p>
                </div>

                {summary.guest_count === 0 ? (
                  <Alert variant="info">No allergy data on record for this invite.</Alert>
                ) : summary.retention_requested ? (
                  <Alert variant="success">
                    You've requested to keep this data. It will not be automatically deleted. Contact us if you change your mind.
                  </Alert>
                ) : (
                  <>
                    <p className="text-sm text-text-secondary">
                      <strong className="text-text-primary">{summary.guest_count} guest{summary.guest_count !== 1 ? 's' : ''}</strong> with allergy data on record.
                    </p>

                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="primary"
                        className="!bg-error hover:!bg-error/90"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        Delete allergy data
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setShowKeepForm(v => !v)}
                      >
                        Keep this data
                      </Button>
                    </div>

                    {showKeepForm && (
                      <div className="border border-border rounded-xl p-4 space-y-4 bg-background">
                        <Select
                          label="Reason for keeping"
                          options={KEEP_REASON_OPTIONS}
                          value={keepReason}
                          onChange={setKeepReason}
                          placeholder="Select a reason..."
                          required
                        />

                        <div className="space-y-1.5">
                          <label className="block text-sm font-medium text-text-primary">
                            Additional notes <span className="text-text-muted font-normal">(optional)</span>
                          </label>
                          <textarea
                            value={keepNotes}
                            onChange={e => setKeepNotes(e.target.value)}
                            rows={3}
                            placeholder="Any extra context..."
                            className="w-full rounded-lg border border-border bg-background-card px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                          />
                        </div>

                        <div className="flex items-center gap-3">
                          <Button
                            variant="primary"
                            onClick={handleKeepSubmit}
                            disabled={!keepReason || keep.isPending}
                          >
                            {keep.isPending ? 'Submitting...' : 'Keep data'}
                          </Button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowKeepForm(false);
                              setKeepReason('');
                              setKeepNotes('');
                            }}
                            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete allergy data?"
        message={
          summary
            ? `This will permanently remove allergy information for all ${summary.guest_count} guests on ${summary.title}. All other guest data — names, RSVP status — will be kept. This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onClose={() => setShowDeleteModal(false)}
        isLoading={purge.isPending}
      />
    </div>
  );
}
